// 📁 src/store/useTodoStore.ts
import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";
import {
  iconGenreBackground,
  iconGenreDance,
  iconGenreHappy,
  iconGenreHiphop,
  iconGenrePop,
  iconGenreRnB,
  iconGenreRock,
  iconGenreRomantic,
  iconGenreSlowrock,
  iconGenreTraditional,
  iconGenreVlog,
  iconGenreVoice,
  iconGenreJazz,
  iconGenreOther,
} from "assets";

interface DialogMusicState {
  musicGenress: any[];
  isLoadingPage: boolean;
  musics: any[];
  musicsRecord: AppRecordJK;
  selectedGenre: any;
  initializePage: () => Promise<void>;
  disposePage: () => Promise<void>;
  fetchMusics: (isRefresh: boolean, searchText: string) => Promise<void>;
  toggleGenre: (genre: any) => Promise<void>;
}

const musicGenres = [
  { name: "Background", icon: iconGenreBackground },
  { name: "Dance", icon: iconGenreDance },
  { name: "Happy", icon: iconGenreHappy },
  { name: "Hip Hop", icon: iconGenreHiphop },
  { name: "Pop", icon: iconGenrePop },
  { name: "R&B", icon: iconGenreRnB },
  { name: "Rock", icon: iconGenreRock },
  { name: "Romatic", icon: iconGenreRomantic },
  { name: "Slow Rock", icon: iconGenreSlowrock },
  { name: "Traditional", icon: iconGenreTraditional },
  { name: "Vlog", icon: iconGenreVlog },
  { name: "Voice", icon: iconGenreVoice },
  { name: "Jazz", icon: iconGenreJazz },
  { name: "Other", icon: iconGenreOther },
  { name: "Electronic", icon: iconGenreOther },
  { name: "Funny", icon: iconGenreOther },
];

const useDialogMusicStore = create<DialogMusicState>((set, get) => ({
  musicGenress: [],
  isLoadingPage: true,
  musics: [],
  selectedGenre: null,
  musicsRecord: new AppRecordJK(),

  initializePage: async () => {
    const { musicsRecord } = get();
    let tmpMusicsRecord = new AppRecordJK({ ...musicsRecord });

    try {
      let url = RouteApi.getMusicWeb;
      url += `token=${Service.token}`;
      url += `&search_text=`;

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const newMusics = responses.songs;

        if (newMusics.next_page_url) {
          tmpMusicsRecord.loadingStatus = AppRecordStatusJK.loaded;
          tmpMusicsRecord.nextPageUrl = newMusics.next_page_url;
        } else {
          tmpMusicsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          tmpMusicsRecord.nextPageUrl = "";
        }

        const genres = structuredClone(responses.music_genress).map((genre: any) => {
          const match = musicGenres.find((element) => element.name === genre.name);
          return {
            ...genre,
            icon: match?.icon ?? null, // this is now the imported image path
          };
        });

        set({
          musicGenress: genres,
          musicsRecord: tmpMusicsRecord,
          musics: [...newMusics.data],
        });
      } else {
        tmpMusicsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ musicsRecord: tmpMusicsRecord });
      }
    } catch (error) {
      tmpMusicsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ musicsRecord: tmpMusicsRecord });

      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ isLoadingPage: false });
    }
  },

  disposePage: async () => {
    try {
      set({
        musicGenress: [],
        isLoadingPage: true,
        musics: [],
        selectedGenre: null,
        musicsRecord: new AppRecordJK(),
      });
    } catch (error) {
      AppLog.show({
        message: `[disposePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchMusics: async (isRefresh: boolean, searchText: string) => {
    const { musics, musicsRecord, selectedGenre } = get();

    let tmpMusicsRecord = new AppRecordJK({ ...musicsRecord });
    let tmpMusics = [...musics];

    try {
      let url = RouteApi.getMusicWeb;
      url += `token=${Service.token}`;
      url += `&search_text=${searchText}`;

      if (isRefresh) {
        if (selectedGenre) {
          url += `&music_genres=${selectedGenre.name}`;
        }
        tmpMusicsRecord.loadingStatus = AppRecordStatusJK.loading;
        tmpMusics = [];

        set({
          musicsRecord: tmpMusicsRecord,
          musics: tmpMusics,
        });
      } else {
        if (tmpMusicsRecord.canLoadMore()) {
          url = tmpMusicsRecord.nextPageUrl!;
          tmpMusicsRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ musicsRecord: tmpMusicsRecord });
        } else {
          return;
        }
      }

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const newMusics = responses.songs;

        if (newMusics.next_page_url) {
          tmpMusicsRecord.loadingStatus = AppRecordStatusJK.loaded;
          tmpMusicsRecord.nextPageUrl = newMusics.next_page_url;
        } else {
          tmpMusicsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
          tmpMusicsRecord.nextPageUrl = "";
        }

        set({
          musicsRecord: tmpMusicsRecord,
          musics: [...tmpMusics, ...newMusics.data],
        });
      } else {
        tmpMusicsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ musicsRecord: tmpMusicsRecord });
      }
    } catch (error) {
      tmpMusicsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ musicsRecord: tmpMusicsRecord });

      AppLog.show({
        message: `[fetchMusics]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  toggleGenre: async (genre: any) => {
    try {
      const { selectedGenre } = get();

      if (genre.id === selectedGenre?.id) {
        set({ selectedGenre: null });
        await useDialogMusicStore.getState().fetchMusics(true, "");
      } else {
        set({ selectedGenre: genre });
        await useDialogMusicStore.getState().fetchMusics(true, "");
      }
    } catch (error) {
      AppLog.show({
        message: `[toggleGenre]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

export default useDialogMusicStore;

